import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import DashboardLayout from "@/layouts/DashboardLayout";
import DashboardPage from "@/pages/DashboardPage";
import LogsPage from "@/pages/LogsPage";
import LogDetailPage from "@/pages/LogDetailPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import BlacklistPage from "@/pages/BlacklistPage";
import AlertsPage from "@/pages/AlertsPage";
import TrackingPage from "@/pages/TrackingPage";
import EmailAnalyzerPage from "@/pages/EmailAnalyzerPage";
import LinkTrackerPage from "@/pages/LinkTrackerPage";
import InvestigatePage from "@/pages/InvestigatePage";
import SubscriptionPage from "@/pages/SubscriptionPage";
import AdminDashboardPage from "@/pages/AdminDashboardPage";
import AdminLicensesPage from "@/pages/AdminLicensesPage";
import AdminAffiliatesPage from "@/pages/AdminAffiliatesPage";
import AdminPayoutsPage from "@/pages/AdminPayoutsPage";
import EmailTrackingPage from "@/pages/EmailTrackingPage";
import PhoneLookupPage from "@/pages/PhoneLookupPage";
import PricingPage from "@/pages/PricingPage";
import InvoiceHistoryPage from "@/pages/InvoiceHistoryPage";
import UserGuidePage from "@/pages/UserGuidePage";
import InvestigatorsGuidePage from "@/pages/InvestigatorsGuidePage";
import HomePage from "@/pages/HomePage";
import HistoryPage from "@/pages/HistoryPage";
import "@/App.css";

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
          <Route element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/logs" element={<LogsPage />} />
            <Route path="/logs/:id" element={<LogDetailPage />} />
            <Route path="/analytics" element={<AnalyticsPage />} />
            <Route path="/blacklist" element={<BlacklistPage />} />
            <Route path="/alerts" element={<AlertsPage />} />
            <Route path="/tracking" element={<TrackingPage />} />
            <Route path="/email-analyzer" element={<EmailAnalyzerPage />} />
            <Route path="/link-tracker" element={<LinkTrackerPage />} />
            <Route path="/investigate" element={<InvestigatePage />} />
            <Route path="/history" element={<HistoryPage />} />
            <Route path="/subscription" element={<SubscriptionPage />} />
            <Route path="/subscription/success" element={<SubscriptionPage />} />
            <Route path="/invoices" element={<InvoiceHistoryPage />} />
            <Route path="/guide" element={<UserGuidePage />} />
            <Route path="/investigators-guide" element={<InvestigatorsGuidePage />} />
            <Route path="/admin" element={<AdminDashboardPage />} />
            <Route path="/admin/licenses" element={<AdminLicensesPage />} />
            <Route path="/admin/affiliates" element={<AdminAffiliatesPage />} />
            <Route path="/admin/payouts" element={<AdminPayoutsPage />} />
            <Route path="/email-tracking" element={<EmailTrackingPage />} />
            <Route path="/phone-lookup" element={<PhoneLookupPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/checkout/success" element={<PricingPage />} />
            <Route path="/checkout/cancel" element={<PricingPage />} />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
